<template>
      <div class="sw-wrapper">
        <div class="sw-sidebar">
        <side-bar></side-bar>
        </div>
        <div class="sw-content">
          <router-view/>
          <footer class="sw-footer">2026 - Moovet App versión 2.0 | Gracias por Innovar con KND Technologies | <a class="sw-footer__link" href="https://knd.mx" target="_blank" rel="noopener noreferrer">knd.mx</a></footer>
        </div>

        <b-modal
          v-model="idleWarningVisible"
          centered
          title="¿Sigues ahí?"
          hide-footer
          modal-class="ew-modal"
        >
          <p class="ew-modal__text">Tu sesión se cerrará en {{ idleCountdownLabel }} por inactividad.</p>
          <div class="ew-modal__actions">
            <button class="ew-btn-primary" @click="staySignedIn">Seguir conectado</button>
          </div>
        </b-modal>
      </div>
</template>

<script>
import Sidebar from './Sidebar'

// Fase 0 del plan de monedero ya cubrió "el token vencido falla en
// silencio"; esto es la otra mitad — hoy una sesión se queda abierta
// indefinidamente mientras el JWT (10 días) no expire, sin importar
// cuánto tiempo lleve la pestaña olvidada en una laptop compartida.
// PanelControl.vue envuelve TODAS las rutas de /dashboard (es su
// componente padre), así que vive montado exactamente mientras hay una
// sesión de panel activa — el lugar natural para este timer.
const IDLE_WARNING_AFTER_MS = 25 * 60 * 1000 // 25 min sin actividad: avisa
const IDLE_LOGOUT_AFTER_MS = 2 * 60 * 1000   // 2 min más sin respuesta: cierra
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart']
const ACTIVITY_THROTTLE_MS = 1000 // mousemove dispara decenas de veces por segundo

export default {
  name: 'Dashboard',
  metaInfo: {
      title: 'SkillWizard'
  },
  data () {
    return {
      idleWarningVisible: false,
      idleSecondsLeft: 0
    }
  },
  computed: {
    idleCountdownLabel () {
      const m = Math.floor(this.idleSecondsLeft / 60)
      const s = this.idleSecondsLeft % 60
      return `${m}:${s < 10 ? '0' : ''}${s}`
    }
  },
  created () {

  },
  mounted () {
    this.lastActivityAt = 0
    this.boundHandleActivity = this.handleActivity.bind(this)
    ACTIVITY_EVENTS.forEach(evt => {
      document.addEventListener(evt, this.boundHandleActivity, { passive: true })
    })
    this.resetIdleTimer()
  },
  beforeDestroy () {
    ACTIVITY_EVENTS.forEach(evt => {
      document.removeEventListener(evt, this.boundHandleActivity, { passive: true })
    })
    this.clearIdleTimers()
  },
  methods: {
    handleActivity () {
      const now = Date.now()
      if (now - this.lastActivityAt < ACTIVITY_THROTTLE_MS) return
      this.lastActivityAt = now
      // Cualquier actividad cuenta como "sigue ahí", incluso con el aviso
      // ya abierto — no hace falta que encuentren y toquen el botón si
      // ya están de vuelta usando la pantalla.
      if (this.idleWarningVisible) this.idleWarningVisible = false
      this.resetIdleTimer()
    },
    resetIdleTimer () {
      this.clearIdleTimers()
      this.warningTimer = setTimeout(this.showIdleWarning, IDLE_WARNING_AFTER_MS)
    },
    showIdleWarning () {
      this.idleWarningVisible = true
      this.idleSecondsLeft = Math.round(IDLE_LOGOUT_AFTER_MS / 1000)
      this.countdownInterval = setInterval(() => {
        this.idleSecondsLeft -= 1
        if (this.idleSecondsLeft <= 0) clearInterval(this.countdownInterval)
      }, 1000)
      this.logoutTimer = setTimeout(this.idleLogout, IDLE_LOGOUT_AFTER_MS)
    },
    staySignedIn () {
      this.idleWarningVisible = false
      this.resetIdleTimer()
    },
    idleLogout () {
      this.clearIdleTimers()
      localStorage.clear()
      this.$router.push({
        path: '/signin',
        query: { redirect: this.$route.fullPath, idle: 'true' }
      })
    },
    clearIdleTimers () {
      clearTimeout(this.warningTimer)
      clearTimeout(this.logoutTimer)
      clearInterval(this.countdownInterval)
    }
  },
  components: {
   'side-bar': Sidebar,
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  @import "../assets/styles/variables";
  @import "../assets/styles/app-tokens";
  @import "../assets/styles/ew-components";

  .sw-container-panel{
    margin: 0;
  }

  // Scoped CSS no hereda entre componentes: aunque ShipmentsList.vue ya
  // tiene estas mismas clases, viven con SU propio hash de scope, no el de
  // este componente — hay que volver a incluir el mixin aquí también. Solo
  // .ew-modal (el contenedor de bootstrap-vue en sí) vive en un <style> sin
  // scope y por eso sí aplica igual en cualquier lado.
  .ew-btn-primary { @include ew-btn-primary; }
  .ew-modal__text {
    font-family: var(--app-font);
    color: var(--app-navy-secondary);
    text-align: left;
  }
  .ew-modal__actions {
    display: flex;
    justify-content: flex-end;
  }
  .sw-wrapper {
    display: flex;
    width: 100%;
    align-items: stretch;
    @include app-tokens;
}

.sw-content {
    width: 100%;
    margin-left: 17rem; // = $sidebar-width en _sidebar-tokens.scss
    min-height: 100vh;
    overflow: hidden;
    background-color: var(--app-canvas);
    display: flex;
    flex-direction: column;
}

.sw-content > :first-child {
  flex: 1 0 auto;
}

.sw-footer {
  flex-shrink: 0;
  padding: 1.1rem 2rem;
  border-top: 1px solid var(--app-border);
  background: #fff;
  font-family: var(--app-font);
  font-size: 0.75rem;
  color: var(--app-muted);
  text-align: center;
}
.sw-footer__link {
  color: inherit;
  text-decoration: none;
  &:hover { color: var(--app-navy); text-decoration: underline; }
}

.sw-sidebar {
  box-shadow: 5px 0px 30px rgba(34, 42, 89, 0.05);
    top: 0;
    bottom: 0;
    position: fixed;
    min-width: 17rem;
    max-width: 17rem;
}

@media (max-width: 768px) {
    // Nota: esto conserva el comportamiento actual (el sidebar se corre casi
    // fuera de pantalla, dejando un pequeño borde visible), solo reescalado
    // al nuevo ancho de 17rem. No es un off-canvas real: hoy no existe ningún
    // botón para volver a abrirlo en móvil. Construir ese toggle es un cambio
    // de comportamiento aparte, fuera de esta pasada de tokens.
    .sw-sidebar {
        margin-left: -15.25rem;
    }
    .sw-content{
        margin-left: 0;
    }
}

</style>



// WEBPACK FOOTER //
// src/components/PanelControl.vue